// App.js (User app)
// URL routing via React Navigation linking
// Safe auth gating for private pages
// Keeps Router context on web
// Includes public creator voucher profile route: /Vouchers/:Promoter

import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';

import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';

// Keep Router context on WEB only.
// This prevents crashes where an existing screen still uses
// react-router-dom Link, useParams, useNavigate, or similar.
import { BrowserRouter as Router } from 'react-router-dom';

// Existing screens
import Homepage from './Homepage';
import CreateSubmissionPage from './CreateSubmissionPage';
import UserRecapPage from './UserRecapPage';
import PublicBooking from './PublicBooking';
import UserCollabDashboard from './UserCollabDashboard';
import CollabCampaignDetailsPage from './CollabCampaignDetailsPage';
import UserGetAssignQRPage from './UserGetAssignQRPage';
import UserGetQRSearch from './UserGetQRSearch';
import QRCodeScannerPage from './QRCodeScannerPage';
import BrandURLLandingPage from './BrandURLLandingPage';
import MediaUpload from './MediaUpload';
import SignUpPage from './SignUpPage';
import LogInPage from './LogInPage';
import CollabCalendar from './CollabCalendar';
import BrandListings from './BrandListings';
import InfluencerInviteLink from './InfluencerInviteLink';
import ManualVisitRedeem from './ManualVisitRedeem';
import VenueDirectory from './VenueDirectory';
import UserActivityFeed from './UserActivityFeed';
import UnclaimedSearch from './UnclaimedSearch';
import SimilarVenues from './SimilarVenues';
import MyFeed from './MyFeed';
import Feed from './Feed';
import PreReleaseListings from './PreReleaseListings';
import OfferMap from './OfferMap';
import Concierge from './Concierge';
import Voucher from './Voucher';
import MyVouchers from './MyVouchers';
import RedeemVoucher from './RedeemVoucher';

// New public creator voucher profile page
import CreatorVouchers from './CreatorVouchers';

const Stack = createStackNavigator();

// Your existing real HTML login URL
const SIGNIN_URL = 'https://collabbay.com/SignIn';

/**
 * Deep linking configuration.
 *
 * Public voucher routes:
 * - /Voucher/:CollabID       = individual voucher purchase page
 * - /Vouchers/:Promoter      = creator public voucher profile page
 */
const linking = {
  prefixes: [
    'https://collabbay.com',
    'https://www.collabbay.com',
    'http://localhost:19006',
    'http://127.0.0.1:19006',
  ],

  config: {
    screens: {
      // Root
      Homepage: '',

      // Public deep links
      CreatorMap: 'creatormap/:Promoter?',
      SignUpPage: 'SignUp/:Referrer?',
      BrandURLLandingPage: 'collab/:CollabID',
      CreateSubmissionPage: 'collaboffer/:ListingID',
      InfluencerInviteLink: 'invite/:InvitationID',
      PublicBooking: 'publicbookings/:BrandName/:CollabID',

      // Public voucher pages
      Voucher: 'Voucher/:CollabID',
      Vouchers: 'Vouchers/:Promoter',
      RedeemVoucher: 'RedeemVoucher/:VoucherID',

      // Authentication route.
      // This deliberately does not use /SignIn because /SignIn is your
      // existing separate HTML login page.
      LogInPage: 'rn-login',

      // Private pages
      UserRecapPage: 'UserRecapPage',
      UserCollabDashboard: 'UserCollabDashboard',
      CollabCampaignDetailsPage: 'CollabCampaignDetailsPage/:CollabID?',
      UserGetAssignQRPage: 'UserGetAssignQRPage/:CollabID?',
      UserGetQRSearch: 'UserGetQRSearch',
      QRCodeScannerPage: 'QRCodeScannerPage',
      MediaUpload: 'MediaUpload/:CollabID?',
      CollabCalendar: 'CollabCalendar',
      BrandListings: 'BrandListings',
      ManualVisitRedeem: 'ManualVisitRedeem/:CollabID?',
      VenueDirectory: 'VenueDirectory',
      UserActivityFeed: 'UserActivityFeed',
      UnclaimedSearch: 'UnclaimedSearch',
      SimilarVenues: 'SimilarVenues/:ListingID?',
      MyFeed: 'MyFeed',
      Feed: 'Feed',
      PreReleaseListings: 'PreReleaseListings',
      OfferMap: 'OfferMap',
      Concierge: 'Concierge',
      MyVouchers: 'MyVouchers',
    },
  },
};

/**
 * Public entry routes.
 *
 * When one of these URLs is opened directly, the page is allowed to render
 * immediately without waiting for a logged-in user session.
 *
 * Important new public route:
 * - /Vouchers/:Promoter
 */
const isPublicEntryPath = () => {
  if (Platform.OS !== 'web') {
    return false;
  }

  const pathname = window?.location?.pathname || '/';

  return (
    /^\/creatormap(?:\/[^/]+)?\/?$/i.test(pathname) ||
    /^\/RedeemVoucher\/[^/]+\/?$/i.test(pathname) ||
    /^\/Voucher\/[^/]+\/?$/i.test(pathname) ||
    /^\/Vouchers\/[^/]+\/?$/i.test(pathname) ||
    /^\/collaboffer\/[^/]+\/?$/i.test(pathname) ||
    /^\/collab\/[^/]+\/?$/i.test(pathname) ||
    /^\/invite\/[^/]+\/?$/i.test(pathname) ||
    /^\/publicbookings\/[^/]+\/[^/]+\/?$/i.test(pathname) ||
    /^\/SignUp(\/.*)?$/i.test(pathname) ||
    /^\/SignIn\/?$/i.test(pathname)
  );
};

/**
 * Full-screen loading state.
 */
const FullscreenLoader = () => {
  return (
    <View style={styles.loadingContainer}>
      <View style={styles.loadingLogoRow}>
        <View style={styles.loadingLogoDot} />

        <View style={styles.loadingLogoTextRow}>
          <View>
            <ActivityIndicator size="large" color="#111111" />
          </View>
        </View>
      </View>
    </View>
  );
};

/**
 * Web login redirect.
 *
 * If internal app navigation tries to open LogInPage on web,
 * the user is sent to the existing HTML sign-in page.
 *
 * On native, the React Native login component remains available.
 */
const WebLoginRedirect = (props) => {
  useEffect(() => {
    if (Platform.OS === 'web') {
      window.location.href = SIGNIN_URL;
    }
  }, []);

  if (Platform.OS === 'web') {
    return <FullscreenLoader />;
  }

  return <LogInPage {...props} />;
};

/**
 * Authentication gate for private screens only.
 *
 * All screens remain registered, which protects deep linking and navigation.
 * Public voucher routes are deliberately not wrapped in this gate.
 */
const AuthGate = ({
  username,
  loadingSession,
  navigation,
  children,
}) => {
  useEffect(() => {
    if (!loadingSession && !username) {
      if (Platform.OS === 'web') {
        window.location.href = SIGNIN_URL;
        return;
      }

      navigation.replace('LogInPage');
    }
  }, [loadingSession, username, navigation]);

  if (loadingSession) {
    return <FullscreenLoader />;
  }

  if (!username) {
    return <FullscreenLoader />;
  }

  return children;
};

/**
 * Main application shell.
 */
const AppShell = () => {
  const [username, setUsername] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);

  /**
   * Public routes, including /Vouchers/:Promoter, do not wait for a session
   * before appearing.
   *
   * Private routes and the homepage retain your existing session behaviour.
   */
  const shouldBlockInitialRender = useMemo(() => {
    return !isPublicEntryPath();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchSessionData = async () => {
      /*
       * Public pages can paint immediately while the session check happens
       * in the background. This keeps shared public voucher links fast.
       */
      if (!shouldBlockInitialRender) {
        setLoadingSession(false);
      }

      try {
        const response = await fetch(
          'https://collabbay.com/APIControllers/GetSessionData.php',
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              Accept: 'application/json',
            },
          }
        );

        const rawText = await response.text();

        let data = null;

        try {
          data = JSON.parse(rawText);
        } catch (parseError) {
          console.error(
            'GetSessionData.php returned invalid JSON:',
            rawText
          );
        }

        if (!cancelled) {
          setUsername(data?.Username || null);
        }
      } catch (error) {
        console.error('Error fetching session data:', error);

        if (!cancelled) {
          setUsername(null);
        }
      } finally {
        if (!cancelled && shouldBlockInitialRender) {
          setLoadingSession(false);
        }
      }
    };

    fetchSessionData();

    return () => {
      cancelled = true;
    };
  }, [shouldBlockInitialRender]);

  /*
   * Private or root entry URLs wait until the session has been checked.
   * Public entry URLs render immediately.
   */
  if (loadingSession && shouldBlockInitialRender) {
    return <FullscreenLoader />;
  }

  return (
    <NavigationContainer
      linking={linking}
      fallback={<FullscreenLoader />}
    >
      <Stack.Navigator
        initialRouteName="Homepage"
        screenOptions={{
          headerShown: false,
          animationEnabled: true,
        }}
      >
        {/*
        |--------------------------------------------------------------------------
        | Public screens
        |--------------------------------------------------------------------------
        | These pages can open without a logged-in user.
        */}

        <Stack.Screen
          name="Homepage"
          component={Homepage}
        />

        <Stack.Screen
          name="CreatorMap"
          getComponent={() => require('./CreatorMap').default}
        />

        <Stack.Screen
          name="LogInPage"
          component={WebLoginRedirect}
        />

        <Stack.Screen
          name="SignUpPage"
          component={SignUpPage}
        />

        <Stack.Screen
          name="BrandURLLandingPage"
          component={BrandURLLandingPage}
        />

        <Stack.Screen
          name="CreateSubmissionPage"
          component={CreateSubmissionPage}
        />

        <Stack.Screen
          name="InfluencerInviteLink"
          component={InfluencerInviteLink}
        />

        <Stack.Screen
          name="PublicBooking"
          component={PublicBooking}
        />

        {/*
        |--------------------------------------------------------------------------
        | Public voucher pages
        |--------------------------------------------------------------------------
        */}

        <Stack.Screen
          name="Voucher"
          component={Voucher}
        />

        <Stack.Screen
          name="Vouchers"
          component={CreatorVouchers}
        />

        <Stack.Screen
          name="RedeemVoucher"
          component={RedeemVoucher}
        />

        {/*
        |--------------------------------------------------------------------------
        | Private screens
        |--------------------------------------------------------------------------
        | These screens remain registered for safe URL routing but their
        | content is protected by AuthGate.
        */}

        <Stack.Screen name="UserRecapPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserRecapPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserCollabDashboard">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserCollabDashboard {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="CollabCampaignDetailsPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <CollabCampaignDetailsPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserGetAssignQRPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserGetAssignQRPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserGetQRSearch">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserGetQRSearch {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="QRCodeScannerPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <QRCodeScannerPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="MediaUpload">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <MediaUpload {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="CollabCalendar">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <CollabCalendar {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="BrandListings">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <BrandListings {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="PreReleaseListings">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <PreReleaseListings {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="OfferMap">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <OfferMap {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="ManualVisitRedeem">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <ManualVisitRedeem {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="VenueDirectory">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <VenueDirectory {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="Concierge">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <Concierge {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="MyVouchers">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <MyVouchers {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserActivityFeed">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserActivityFeed {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UnclaimedSearch">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UnclaimedSearch {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="SimilarVenues">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <SimilarVenues {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="MyFeed">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <MyFeed {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="Feed">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <Feed {...props} />
            </AuthGate>
          )}
        </Stack.Screen>
      </Stack.Navigator>
    </NavigationContainer>
  );
};

/**
 * Root app.
 *
 * Router remains wrapped around the application on web only,
 * matching your existing setup and protecting screens that still use
 * react-router-dom internally.
 */
const App = () => {
  if (Platform.OS === 'web') {
    return (
      <Router>
        <AppShell />
      </Router>
    );
  }

  return <AppShell />;
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    backgroundColor: '#f5f4f1',
    alignItems: 'center',
    justifyContent: 'center',
  },

  loadingLogoRow: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  loadingLogoDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FDF05D',
    marginBottom: 18,
  },

  loadingLogoTextRow: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default App;